
import React from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TAB_DEFINITIONS } from './tabs/tabDefinitions';
import OptimizedTabContent from './tabs/OptimizedTabContent';

interface OptimizedAnalyticsTabsProps {
  pageStats: any[];
  actionStats: any[];
  dailyStats: any[];
  platformStats: any[];
  browserStats: any[];
  osStats: any[];
  timeStats: any[];
  languageStats: any[];
  loadingPageStats: boolean;
  loadingActionStats: boolean;
  loadingDailyStats: boolean;
  loadingPlatformStats: boolean;
  loadingBrowserStats: boolean;
  loadingOSStats: boolean;
  loadingTimeStats: boolean;
  loadingLanguageStats: boolean;
  exportCSV: (data: any[], filename: string) => void;
  COLORS: string[];
  referrerStats: any[];
  loadingReferrerStats: boolean;
  exitPages: any[];
  loadingExitPages: boolean;
  utmStats: any[];
  loadingUTMStats: boolean;
}

const OptimizedAnalyticsTabs = React.memo(({
  pageStats,
  actionStats,
  dailyStats,
  platformStats,
  browserStats,
  osStats,
  timeStats,
  languageStats,
  loadingPageStats,
  loadingActionStats,
  loadingDailyStats,
  loadingPlatformStats,
  loadingBrowserStats,
  loadingOSStats,
  loadingTimeStats,
  loadingLanguageStats,
  exportCSV,
  COLORS,
  referrerStats,
  loadingReferrerStats,
  exitPages,
  loadingExitPages,
  utmStats,
  loadingUTMStats
}: OptimizedAnalyticsTabsProps) => {
  
  // Memoize tab data and loading state getters
  const getTabData = React.useCallback((tabId: string) => {
    switch (tabId) {
      case 'pages': return pageStats;
      case 'actions': return actionStats;
      case 'daily': return dailyStats;
      case 'platform': return platformStats;
      case 'browser': return browserStats;
      case 'os': return osStats;
      case 'time': return timeStats;
      case 'language': return languageStats;
      case 'sources': return referrerStats;
      case 'exitpages': return exitPages;
      case 'campaigns': return utmStats;
      default: return [];
    }
  }, [pageStats, actionStats, dailyStats, platformStats, browserStats, osStats, timeStats, languageStats, referrerStats, exitPages, utmStats]);
  
  const getTabLoading = React.useCallback((tabId: string) => {
    switch (tabId) {
      case 'pages': return loadingPageStats;
      case 'actions': return loadingActionStats;
      case 'daily': return loadingDailyStats;
      case 'platform': return loadingPlatformStats;
      case 'browser': return loadingBrowserStats;
      case 'os': return loadingOSStats;
      case 'time': return loadingTimeStats;
      case 'language': return loadingLanguageStats;
      case 'sources': return loadingReferrerStats;
      case 'exitpages': return loadingExitPages;
      case 'campaigns': return loadingUTMStats;
      default: return false;
    }
  }, [
    loadingPageStats,
    loadingActionStats,
    loadingDailyStats,
    loadingPlatformStats,
    loadingBrowserStats,
    loadingOSStats,
    loadingTimeStats,
    loadingLanguageStats,
    loadingReferrerStats,
    loadingExitPages,
    loadingUTMStats
  ]);

  return (
    <div className="space-y-6 w-full">
      <Tabs defaultValue="pages" className="w-full">
        {/* Mobile-optimized tab navigation */}
        <div className="w-full overflow-x-auto pb-2" style={{ touchAction: 'pan-x' }}>
          <TabsList className="inline-flex h-10 items-center justify-start rounded-md bg-muted p-1 text-muted-foreground min-w-full md:w-auto">
            {TAB_DEFINITIONS.map(({ id, label, icon: Icon }) => (
              <TabsTrigger 
                key={id} 
                value={id} 
                className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-2 md:px-3 py-1.5 text-xs md:text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm gap-1 md:gap-2 flex-shrink-0 min-w-0"
              >
                <Icon className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
                <span className="hidden sm:block">{label}</span>
                <span className="block sm:hidden text-xs truncate">{label.split(' ')[0]}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="w-full">
          {TAB_DEFINITIONS.map(({ id }) => (
            <OptimizedTabContent
              key={id}
              tabId={id}
              isLoading={getTabLoading(id)}
              data={getTabData(id)}
              exportCSV={exportCSV}
              colors={COLORS}
            />
          ))}
        </div>
      </Tabs>
    </div>
  );
});

OptimizedAnalyticsTabs.displayName = 'OptimizedAnalyticsTabs';

export default OptimizedAnalyticsTabs;
